import { DownloadOutlined, FileTextOutlined } from "@ant-design/icons";
import { Alert, Button, Collapse, Modal, Row, Select, Steps, Table, Tooltip, message } from "antd";
import { getColumnSearchProps } from "common/antd-utils";
import { DATE_TIME_FORMAT } from "common/constants";
import { camelToWords, downloadFileFromUrl, snakeToCamelCase } from "common/utils";
import { useFetchMeQuery } from "features/auth/authAPI";
import { useFetchAllBackupDatesQuery } from "features/backupDates/backupDatesAPI";
import { useFetchCreditorQuery } from "features/creditors/agencyPortal/creditorsAPI";
import {
  useFetchImportHistoryQuery,
  usePostDataImportCreateMutation,
  usePostMultirowDataImportCreateMutation,
} from "features/dataImport/agencyPortal/dataImportAPI";
import AccountUploadParamsForm from "features/dataImport/components/accountUploadParamsForm";
import MultirowFileUpload from "features/dataImport/components/multirowFileUpload";
import MultirowUploadParamsForm from "features/dataImport/components/multirowUploadParamsForm";
import {
  useFetchDebtorAssetCategoriesDromoSchemaQuery,
  useFetchDebtorAssetCustomFieldsDromoSchemaQuery,
} from "features/debtorAssets/debtorAssetsAPI";
import { useFetchProductOfferingsQuery } from "features/productOfferings/productOfferingsAPI";
import { useFetchUserSummariesQuery } from "features/userManagementTable/userManagementAPI";
import { useFetchAllAccountUdfCustomFieldsDromoSchemaQuery } from "features/userDefinedFields/accountUserDefinedFieldsAPI";
import { useFetchAllDebtorUdfCustomFieldsDromoSchemaQuery } from "features/userDefinedFields/agencyPortal/debtorUserDefinedFieldsAPI";
import {
  DromoButton,
  getAccountSchemaFields,
  getMultipleCreditorAccountsSchemaFields,
} from "integrations/dromo";
import getAccountUpdateSchemaFields from "integrations/dromo/accountUpdateSchemaFields";
import accountFilteringImportSchemaFields from "integrations/dromo/accountFilteringImportSchemaFields";
import debtorFilteringImportSchemaFields from "integrations/dromo/debtorFilteringImportSchemaFields";
import adjustmentSchemaFields from "integrations/dromo/adjustmentSchemaFields";
import debtorUpdateSchemaFields from "integrations/dromo/debtorUpdateSchemaFields";
import deleteAccountSchemaFields from "integrations/dromo/deleteAccountSchemaFields";
import deleteDebtorEmailsSchemaFields from "integrations/dromo/deleteDebtorEmailsSchemaFields";
import dialerTCNSchemaFields from "integrations/dromo/dialerTCNSchemaFields";
import { convertToDromoCustomField } from "integrations/dromo/dromoUtils";
import lexisNexisSchemaFields from "integrations/dromo/lexisNexisSchemaFields";
import osDialSchemaFields from "integrations/dromo/osDialSchemaFields";
import paymentSchemaFields from "integrations/dromo/paymentSchemaFields";
import paymentToCreditorSchemaFields from "integrations/dromo/paymentToCreditorSchemaFields";
import paymentToForwardingAgencySchemaFields from "integrations/dromo/paymentToForwardingAgencySchemaFields";
import communicationLogsNotesSchemaFields from "integrations/dromo/communicationLogsNotesSchemaFields";
import moment from "moment-timezone";
import { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { getCodebtorCustomFieldsSchema } from "integrations/dromo/debtorSchemaFields";
import {
  convertToDebtorAssetDromoCustomField,
  getDebtorAssetSchemaFields,
} from "integrations/dromo/debtorAssetSchemaFields";
import { PERMISSIONS, useAuthorization } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";

const { Panel } = Collapse;

const StyledCollapse = styled(Collapse)`
  margin-top: 16px;
  background-color: #f5f5f5;
`;

const StyledPanel = styled(Panel)`
  .ant-collapse-header {
    align-items: center !important;
    padding-bottom: 0 !important;
    padding-left: 8px !important;
  }

  .ant-collapse-content-box {
    padding-top: 0 !important;
  }
`;

const StyledSelect = styled(Select)`
  width: 350px;
  margin-left: 4px;
`;

const StyledGlossarySelect = styled(Select)`
  margin-left: 4px;
`;

const StyledFileTextOutlined = styled(FileTextOutlined)`
  margin-left: 4px;
  cursor: pointer;
  color: #1890ff;
`;

const StyledTable = styled(Table)`
  margin-top: 16px;
`;

const StyledSpan = styled.span`
  margin-left: 8px;
`;

const StyledIndicator = styled.span`
  margin-right: 4px;
`;

const StyledLinkButton = styled(Button)`
  padding: 0;
`;

const MULTIROW_PROVIDERS = [
  {
    type: "account_epic",
    label: "Upload Accounts - EPIC",
    providerLabel: "Epic",
    permission: PERMISSIONS.DATA_IMPORT__CREATE__ACCOUNT_EPIC,
  },
  {
    type: "account_meditech",
    label: "Upload Accounts - Meditech",
    providerLabel: "Meditech",
    permission: PERMISSIONS.DATA_IMPORT__CREATE__ACCOUNT_MEDITECH,
  },
  {
    type: "account_meditech_magic",
    label: "Upload Accounts - Meditech Magic",
    providerLabel: "Meditech Magic",
    permission: PERMISSIONS.DATA_IMPORT__CREATE__ACCOUNT_MEDITECH_MAGIC,
  },
  {
    type: "account_meditech_expanse",
    label: "Upload Accounts - Meditech Expanse",
    providerLabel: "Meditech Expanse",
    permission: PERMISSIONS.DATA_IMPORT__CREATE__ACCOUNT_MEDITECH_EXPANSE,
  },
  {
    type: "account_avista",
    label: "Upload Accounts - Avista",
    providerLabel: "Avista",
    permission: PERMISSIONS.DATA_IMPORT__CREATE__ACCOUNT_AVISTA,
  },
];

export default function DataImport() {
  const [uploadType, setUploadType] = useState(null);
  const [glossaryUploadType, setGlossaryUploadType] = useState("payment_to_agency");

  const [postDataImportCreate] = usePostDataImportCreateMutation();
  const [postMultirowDataImportCreate, { isLoading: isMultirowImportLoading }] =
    usePostMultirowDataImportCreateMutation();
  const {
    data: {
      [PERMISSIONS.USER__GET]: isUserGetAuthorized,
      [PERMISSIONS.DATA_IMPORT__GET]: isDataImportGetAuthorized,
    },
  } = useAuthorizations();
  const { data: importHistoryData } = useFetchImportHistoryQuery();
  const { data: users } = useFetchUserSummariesQuery(
    {},
    { skip: !isUserGetAuthorized || !isDataImportGetAuthorized },
  );
  const userIdToName = useMemo(() => {
    const map = {};
    (users ?? []).forEach((user) => {
      map[user.id] = user.name;
    });
    return map;
  }, [users]);
  const { data: isAccountConsumerUpsertAuthorized } = useAuthorization(
    PERMISSIONS.DATA_IMPORT__CREATE__ACCOUNT_CONSUMER_UPSERT,
  );
  const { data: isEpicImportAuthorized } = useAuthorization(
    PERMISSIONS.DATA_IMPORT__CREATE__ACCOUNT_EPIC,
  );
  const { data: isMeditechImportAuthorized } = useAuthorization(
    PERMISSIONS.DATA_IMPORT__CREATE__ACCOUNT_MEDITECH,
  );
  const { data: isMeditechMagicImportAuthorized } = useAuthorization(
    PERMISSIONS.DATA_IMPORT__CREATE__ACCOUNT_MEDITECH_MAGIC,
  );
  const { data: isMeditechExpanseImportAuthorized } = useAuthorization(
    PERMISSIONS.DATA_IMPORT__CREATE__ACCOUNT_MEDITECH_EXPANSE,
  );
  const { data: isAvistaImportAuthorized } = useAuthorization(
    PERMISSIONS.DATA_IMPORT__CREATE__ACCOUNT_AVISTA,
  );

  const multirowAuthorizations = useMemo(
    () => ({
      account_epic: isEpicImportAuthorized,
      account_meditech: isMeditechImportAuthorized,
      account_meditech_magic: isMeditechMagicImportAuthorized,
      account_meditech_expanse: isMeditechExpanseImportAuthorized,
      account_avista: isAvistaImportAuthorized,
    }),
    [
      isEpicImportAuthorized,
      isMeditechImportAuthorized,
      isMeditechMagicImportAuthorized,
      isMeditechExpanseImportAuthorized,
      isAvistaImportAuthorized,
    ],
  );
  const [additionalData, setAdditionalData] = useState(null);
  const { data: debtorCustomFieldsDromoSchema } =
    useFetchAllDebtorUdfCustomFieldsDromoSchemaQuery();
  const { data: accountCustomFieldsDromoSchema } =
    useFetchAllAccountUdfCustomFieldsDromoSchemaQuery();
  const { data: productOfferings } = useFetchProductOfferingsQuery();
  const { data: debtorAssetCategoriesDromoSchema } =
    useFetchDebtorAssetCategoriesDromoSchemaQuery();
  const { data: debtorAssetCustomFieldsDromoSchema } =
    useFetchDebtorAssetCustomFieldsDromoSchemaQuery();
  const [isColumnGlossaryModalOpen, setIsColumnGlossarysModalOpen] = useState(false);

  const { data: selectedCreditor, isFetching } = useFetchCreditorQuery(
    { creditorId: additionalData?.creditorId },
    { skip: !additionalData?.creditorId },
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [errorFile, setErrorFile] = useState(null);
  const { data: me } = useFetchMeQuery();
  const { data: backupDates } = useFetchAllBackupDatesQuery();

  const isAccountUploadType = [
    "account_commercial",
    "account_consumer",
    "account_consumer_upsert",
  ].includes(uploadType);
  const isMultipleClientsAccountUploadType = [
    "account_multiple_clients_consumer",
    "account_multiple_clients_commercial",
  ].includes(uploadType);
  const multirowProvider = MULTIROW_PROVIDERS.find((p) => p.type === uploadType);
  const isMultirowUploadType = !!multirowProvider;

  const submitCsv = async (data, { id, filename }) => {
    setErrorFile(null);
    const result = await postDataImportCreate({
      // filename is "null" when the user enters data manually, so we fallback
      // to {id}.json as the filename (same returned by dromo)
      filename: filename || `${id}.json`,
      uploadId: id,
      type: uploadType,
      ...additionalData,
    });

    const reset = () => {
      setCurrentStep(0);
      setUploadType(null);
      setAdditionalData(null);
    };

    if ("data" in result) {
      if (result.data.errorFile) {
        setErrorFile(result.data.errorFile);
        message.info("Data imported with errors");
      } else if (result.data.error) {
        message.error(result.data.error);
      } else {
        message.success("Data imported successfully");
        reset();
      }
    }

    if ("error" in result) {
      message.error("Failed to import data");
    }
  };

  const submitMultirowImport = async (file) => {
    setErrorFile(null);

    const result = await postMultirowDataImportCreate({
      file,
      values: {
        filename: file.name,
        type: uploadType,
        creditorId: additionalData.creditorId,
        cbrClassCode: additionalData.cbrClassCode,
        productOfferingId: additionalData.productOfferingId,
        useAsyncJob: true,
      },
    });

    const reset = () => {
      setCurrentStep(0);
      setUploadType(null);
      setAdditionalData(null);
    };

    if ("data" in result) {
      if (result.data.errorFile) {
        setErrorFile(result.data.errorFile);
        message.info("Data imported with errors");
      } else if (result.data.error) {
        message.error(result.data.error);
      } else {
        message.success(
          `${multirowProvider.providerLabel} import started. Check import history for status.`,
        );
        reset();
      }
    }
  };

  const onTypeSelect = (value) => {
    setUploadType(value);
    setGlossaryUploadType(value);
    setCurrentStep(1);
  };

  const onAdditionalDataSubmit = (data) => {
    setAdditionalData(data);
    setCurrentStep(2);
  };

  const isValidAccountUpload = isAccountUploadType
    ? additionalData?.creditorId && additionalData?.cbrClassCode
    : true;

  const isDromoButton = me && uploadType && isValidAccountUpload;

  const importTypes = useMemo(() => {
    const accountCustomFieldsSchema = (accountCustomFieldsDromoSchema ?? [])
      // if a field is not assigned to the selected creditor, we mark it as not required. (We still want to show them)
      .map((field) => ({
        ...field,
        isRequired: field.isRequired,
      }))
      .map(convertToDromoCustomField)
      .map((field) => ({
        ...field,
        label: `${field.label} - Account UDF`,
      }));
    const debtorCustomFieldsSchema = (debtorCustomFieldsDromoSchema ?? [])
      .map(convertToDromoCustomField)
      .map((field) => ({
        ...field,
        label: `${field.label} - Debtor UDF`,
      }));
    const backupDatesRequired =
      backupDates?.filter((item) => item.isRequired).map((item) => item.systemDateField) ?? [];

    // Debtor asset custom fields schema
    const debtorAssetCustomFieldsSchema = (debtorAssetCustomFieldsDromoSchema ?? [])
      .map(convertToDebtorAssetDromoCustomField)
      .map((field) => ({
        ...field,
        label: `${field.label} - Asset UDF`,
      }));

    // Asset schema fields for 3 debtors x 3 assets each
    const assetSchemaFields = getDebtorAssetSchemaFields(
      3, // numberOfDebtors
      3, // assetsPerDebtor
      debtorAssetCategoriesDromoSchema ?? [],
      debtorAssetCustomFieldsSchema,
    );

    const bulkActionOptions = [
      {
        label: "Import Accounts For Bulk Actions",
        value: "bulk_action_account",
        schema: [...accountFilteringImportSchemaFields],
      },
      {
        label: "Import Debtors For Bulk Actions",
        value: "bulk_action_debtor",
        schema: [...debtorFilteringImportSchemaFields],
      },
    ];
    const paymentAdjustmentOptions = [
      {
        label: "Upload Payments to Agency",
        value: "payment_to_agency",
        schema: paymentSchemaFields,
      },
      {
        label: "Upload Payments to Creditor",
        value: "payment_to_creditor",
        schema: paymentToCreditorSchemaFields,
      },
      {
        label: "Upload Payments to Forwarding Agency",
        value: "payment_to_forwarding_agency",
        schema: paymentToForwardingAgencySchemaFields,
      },
      {
        label: "Upload Adjustments",
        value: "adjustment",
        schema: adjustmentSchemaFields,
      },
    ];
    const communicationOptions = [
      {
        label: "Upload Communication Logs",
        value: "debtor_communication_history",
        schema: communicationLogsNotesSchemaFields,
      },
      {
        label: "TCN Dialer Import",
        value: "dialer_import_tcn",
        schema: dialerTCNSchemaFields,
      },
      {
        label: "OSDial Import",
        value: "dialer_import_osdial",
        schema: osDialSchemaFields,
      },
    ];

    const accountDebtorOptions = [
      {
        label: "Upload Account - Consumer",
        value: "account_consumer",
        schema: [
          ...getAccountSchemaFields("consumer", backupDatesRequired),
          ...accountCustomFieldsSchema,
          ...getCodebtorCustomFieldsSchema(debtorCustomFieldsSchema, 3),
          ...assetSchemaFields,
        ],
      },
      {
        label: "Upload Account - Commercial",
        value: "account_commercial",
        schema: [
          ...getAccountSchemaFields("commercial", backupDatesRequired),
          ...accountCustomFieldsSchema,
          ...getCodebtorCustomFieldsSchema(debtorCustomFieldsSchema, 3),
          ...assetSchemaFields,
        ],
      },
      {
        label: "Upload Multiple Clients Account - Consumer",
        value: "account_multiple_clients_consumer",
        schema: [
          ...getMultipleCreditorAccountsSchemaFields(
            {
              accountType: "consumer",
              productOfferings,
            },
            backupDatesRequired,
          ),
          ...accountCustomFieldsSchema,
          ...getCodebtorCustomFieldsSchema(debtorCustomFieldsSchema, 3),
          ...assetSchemaFields,
        ],
      },
      {
        label: "Upload Multiple Clients Account - Commercial",
        value: "account_multiple_clients_commercial",
        schema: [
          ...getMultipleCreditorAccountsSchemaFields(
            {
              accountType: "commercial",
              productOfferings,
            },
            backupDatesRequired,
          ),
          ...accountCustomFieldsSchema,
          ...getCodebtorCustomFieldsSchema(debtorCustomFieldsSchema, 3),
          ...assetSchemaFields,
        ],
      },
      {
        label: "Update Accounts",
        value: "account_update",
        schema: [
          // The `accountType` field is used to add debtor related fields, which is disabled for now
          // Passing in "base", which is neither "consumer" nor "commercial", and the internal logic
          // will only keep the account related fields
          ...getAccountUpdateSchemaFields("base", backupDatesRequired),
          ...accountCustomFieldsSchema.map((field) => ({
            ...field,
            isRequired: false,
          })),
        ],
      },
      {
        label: "Debtor Update",
        value: "debtor_update",
        schema: [
          ...debtorUpdateSchemaFields,
          ...(debtorCustomFieldsDromoSchema ?? [])
            .map((field) => ({
              ...field,
              isRequired: false, // We don't want to require custom fields for debtor update
            }))
            .map(convertToDromoCustomField)
            .map((field) => ({
              ...field,
              label: `${field.label} - Debtor UDF`,
            })),
        ],
      },
      {
        label: "Delete Accounts",
        value: "account_delete",
        schema: deleteAccountSchemaFields,
      },
      {
        label: "Delete Accounts Without Payments",
        value: "account_delete_without_payment",
        schema: deleteAccountSchemaFields,
      },
      {
        label: "LexisNexis Phone Import",
        value: "lexis_nexis",
        schema: lexisNexisSchemaFields,
      },
      {
        label: "Delete Debtor Emails",
        value: "debtor_email_delete",
        schema: deleteDebtorEmailsSchemaFields,
      },
    ];

    if (isAccountConsumerUpsertAuthorized) {
      accountDebtorOptions.push({
        label: "Full Data Debtor/Account Upsert - Consumer",
        value: "account_consumer_upsert",
        schema: [
          ...getAccountSchemaFields("consumer", backupDatesRequired, "account_consumer_upsert"),
          ...accountCustomFieldsSchema,
          ...getCodebtorCustomFieldsSchema(debtorCustomFieldsSchema, 3),
          ...assetSchemaFields,
        ],
      });
    }

    MULTIROW_PROVIDERS.forEach((provider) => {
      if (multirowAuthorizations[provider.type]) {
        accountDebtorOptions.push({
          label: provider.label,
          value: provider.type,
          schema: [],
        });
      }
    });

    return [
      {
        label: <span>Account/Debtor Management</span>,
        options: accountDebtorOptions,
      },
      {
        label: "Payments and Adjustments",
        options: paymentAdjustmentOptions,
      },
      {
        label: "Communication Imports",
        options: communicationOptions,
      },
      { label: "Bulk Actions", options: bulkActionOptions },
    ];
  }, [
    accountCustomFieldsDromoSchema,
    debtorCustomFieldsDromoSchema,
    debtorAssetCategoriesDromoSchema,
    debtorAssetCustomFieldsDromoSchema,
    productOfferings,
    backupDates,
    isAccountConsumerUpsertAuthorized,
    multirowAuthorizations,
  ]);

  const flattenedImportTypes = importTypes.reduce((acc, group) => {
    return [...acc, ...group.options];
  }, []);

  const steps = [
    {
      title: "Choose Action",
      description: (
        <StyledSelect
          showSearch
          filterOption={(input, option) =>
            (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
          }
          popupMatchSelectWidth={false}
          placeholder="Select one..."
          value={uploadType}
          onSelect={onTypeSelect}
          options={importTypes}
        />
      ),
    },
    (isAccountUploadType || isMultipleClientsAccountUploadType) && {
      title: isAccountUploadType ? "Select Client" : "Select Parent Client",
      description: (
        <AccountUploadParamsForm
          onSubmit={onAdditionalDataSubmit}
          isFetching={isFetching}
          isMultipleClientsAccountUploadType={isMultipleClientsAccountUploadType}
          isAccountCreateUpdateUploadType={uploadType === "account_consumer_upsert"}
        />
      ),
    },
    isMultirowUploadType && {
      title: "Select Client",
      description: (
        <MultirowUploadParamsForm
          onSubmit={onAdditionalDataSubmit}
          isFetching={isFetching}
          providerLabel={multirowProvider.providerLabel}
        />
      ),
    },
    isMultirowUploadType
      ? {
          title: "Upload File",
          description: additionalData?.creditorId && (
            <MultirowFileUpload
              onSubmit={submitMultirowImport}
              isLoading={isMultirowImportLoading}
              providerLabel={multirowProvider.providerLabel}
            />
          ),
        }
      : {
          title: (
            <>
              Upload File
              <Tooltip title="See Columns Glossary">
                <StyledFileTextOutlined onClick={() => setIsColumnGlossarysModalOpen(true)} />
              </Tooltip>
            </>
          ),
          description: isDromoButton && (
            <DromoButton
              fields={flattenedImportTypes.find((type) => type.value === uploadType)?.schema ?? []}
              text="Click to Upload"
              onResults={submitCsv}
              importIdentifier={`accounts-agency:${me.agencyId}-type:${uploadType}`}
            />
          ),
        },
  ];

  const columns = [
    {
      title: "File Name",
      dataIndex: "filename",
      key: "filename",
      render: (filename, record) =>
        record.file ? (
          <StyledLinkButton type="link" onClick={() => downloadFileFromUrl(record.file, filename)}>
            {filename}
          </StyledLinkButton>
        ) : (
          filename
        ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => camelToWords(snakeToCamelCase(type)),
    },
    {
      title: "Upload Time",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => moment(date).format(DATE_TIME_FORMAT),
    },
    {
      title: "Uploaded By",
      dataIndex: "createdById",
      key: "createdById",
      render: (createdById) => userIdToName[createdById] ?? "",
    },
    {
      title: "# of Records Affected",
      dataIndex: "numberOfRowsImported",
      key: "numberOfRowsImported",
      render: (numberOfRowsImported) => numberOfRowsImported ?? "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        return (
          <span>
            {camelToWords(snakeToCamelCase(status))}
            {record.errorFile && (
              <Button
                icon={<DownloadOutlined />}
                type="link"
                onClick={() => downloadFileFromUrl(record.errorFile)}
              >
                Download Error File
              </Button>
            )}
          </span>
        );
      },
    },
  ];

  const [filteredInfo, setFilteredInfo] = useState({});
  const searchInput = useRef(null);
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };
  const handleReset = (clearFilters) => {
    clearFilters();
  };
  const handleChange = (_, filters) => {
    setFilteredInfo(filters);
  };

  const handleCancel = () => {
    setIsColumnGlossarysModalOpen(false);
  };

  return (
    <div>
      <Steps direction="vertical" current={currentStep} items={steps.filter(Boolean)} />
      {errorFile && (
        <Alert
          type="warning"
          message={
            <span>
              Data may be partially uploaded. Click <a href={errorFile}>here</a> to download the
              list of errors for rows that were not uploaded
            </span>
          }
        />
      )}
      <StyledCollapse defaultActiveKey="history" bordered={false}>
        <StyledPanel header={<h3>Import History</h3>} key="history">
          <Table
            scroll={{ x: "max-content" }}
            columns={columns}
            bordered
            dataSource={importHistoryData}
          />
        </StyledPanel>
      </StyledCollapse>
      {isColumnGlossaryModalOpen && (
        <Modal
          title="Columns Glossary"
          open={isColumnGlossaryModalOpen}
          footer={null}
          onCancel={handleCancel}
        >
          <Row align="middle">
            <span>Upload Type: </span>
            <StyledGlossarySelect
              showSearch
              filterOption={(input, option) =>
                (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
              }
              popupMatchSelectWidth={false}
              placeholder="Select Upload Type..."
              value={glossaryUploadType}
              // @ts-ignore
              onChange={(value) => setGlossaryUploadType(value)}
              options={importTypes}
            />
          </Row>
          <StyledTable
            size="small"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 30, 100],
            }}
            columns={[
              // @ts-ignore
              {
                title: (
                  <div>
                    <span>Available Columns</span>
                    <StyledSpan>(* indicates a required field)</StyledSpan>
                  </div>
                ),
                dataIndex: "label",
                key: "label",
                render: (label, record) => (
                  <span>
                    {/* @ts-ignore */}
                    {record.validators?.[0]?.validate === "required" && (
                      <StyledIndicator>*</StyledIndicator>
                    )}
                    {label}
                  </span>
                ),
                ...getColumnSearchProps("label", searchInput, handleSearch, handleReset),
                // @ts-ignore
                filteredValue: filteredInfo?.label ?? null,
                sorter: (a, b) => a.label.localeCompare(b.label),
                sortDirections: ["descend", "ascend"],
              },
            ]}
            bordered
            dataSource={
              flattenedImportTypes.find((type) => type.value === glossaryUploadType)?.schema ?? []
            }
            onChange={handleChange}
          />
        </Modal>
      )}
    </div>
  );
}
